// src/features/statistics/components/AgeBarChart.tsx

import { useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { cn } from '../../../components/ui/utils';

import type { AgeGroup } from '../types/audience';

type GenderKey = 'all' | 'male' | 'female';

type Props = {
  ageGroups: AgeGroup[];
};

const TABS: { key: GenderKey; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'male', label: 'Мужчины' },
  { key: 'female', label: 'Женщины' },
];

export function AgeBarChart({ ageGroups }: Props): JSX.Element {
  const [activeGender, setActiveGender] = useState<GenderKey>('all');

  if (ageGroups.length === 0) {
    return (
      <div className="flex items-center justify-center h-full min-h-[120px] text-sm text-muted-foreground">
        Нет данных
      </div>
    );
  }

  const chartData = [...ageGroups].reverse().map(g => ({
    range: g.range,
    value: g[activeGender],
  }));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveGender(tab.key)}
            className={cn(
              'px-2 py-0.5 text-xs rounded-md transition-colors',
              activeGender === tab.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
          <XAxis
            type="number"
            domain={[0, 100]}
            tickFormatter={(v: number) => `${v}%`}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            type="category"
            dataKey="range"
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
            width={36}
          />
          <Tooltip
            formatter={(value: number) => [`${value}%`, 'Доля']}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            cursor={{ fill: 'hsl(var(--muted))', radius: 4 }}
          />
          <Bar dataKey="value" fill="#3b82f6" radius={[0, 3, 3, 0]} maxBarSize={16} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
